/**
 * LandingOverlays — glassmorphic, luminous overlays rendered on the landing
 * page when the user clicks "Studio" or "Pricing" in the nav.
 *
 * Design intent:
 *   - Rounded pill/card with a subtle animated conic border (luminous profile)
 *   - Dark glass background (backdrop-blur), soft shadow, prominent CTA
 *   - Appears BETWEEN the nav and the big "MediaLabs" title
 *   - Dismissable (X) or by clicking the same nav item again
 */
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, Sparkles, Zap, Crown, ArrowRight } from 'lucide-react';
import './landing-overlays.css';

const FADE = {
  initial: { opacity: 0, y: -16, scale: 0.96 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit:    { opacity: 0, y: -12, scale: 0.97 },
  transition: { duration: 0.35, ease: [0.16, 1, 0.3, 1] },
};

/* ============================================================
   STUDIO — Bienvenida
   ============================================================ */
export const StudioOverlay = ({ onClose, onCTA }) => (
  <motion.div
    {...FADE}
    className="landing-overlay-card"
    role="dialog"
    aria-labelledby="studio-overlay-title"
    data-testid="landing-overlay-studio"
  >
    <div className="landing-overlay-glow" aria-hidden="true" />
    <button
      className="landing-overlay-close"
      onClick={onClose}
      data-testid="landing-overlay-close"
      aria-label="Cerrar"
    >
      <X size={16} />
    </button>

    <div className="landing-overlay-eyebrow">
      <Sparkles size={14} />
      <span>Studio MediaLabs</span>
    </div>

    <h2 id="studio-overlay-title" className="landing-overlay-title">
      Bienvenid@ a Studio MediaLabs
    </h2>

    <p className="landing-overlay-body">
      <strong>El siguiente nivel en la generación multimedia.</strong>
      <br />
      Crearás videos sensacionales con total control y sin límites para tu imaginación.
    </p>

    <p className="landing-overlay-body subtle">
      Un laboratorio completo de inteligencia artificial para experimentar y crear contenido
      multimedia de forma fluida y escalable — con los últimos avances tecnológicos en tus manos.
    </p>

    <p className="landing-overlay-body subtle">
      Hemos creado una herramienta sin igual, sin límites de tiempo, que te enamorará
      y expandirá tu mundo hasta donde quieras llegar.
    </p>

    <button
      className="landing-overlay-cta primary"
      onClick={onCTA}
      data-testid="landing-overlay-studio-cta"
    >
      Comienza a crear gratis
      <span className="landing-overlay-cta-arrow">
        <ArrowRight size={16} />
      </span>
    </button>
  </motion.div>
);

/* ============================================================
   PRICING
   ============================================================ */
const PLANS = [
  {
    id: 'free',
    featured: true,
    name: 'Inicio Gratis',
    tagline: 'Plan freemium para que nos conozcas',
    priceLabel: 'Gratis',
    priceSub: 'sin tarjeta',
    Icon: Sparkles,
    features: [
      '40 créditos de bienvenida, una sola vez',
      'Acceso a todas las herramientas del Studio',
      'Canvas de nodos ilimitado en tiempo',
      'Galería privada con tus generaciones',
    ],
    cta: 'Comenzar gratis',
  },
  {
    id: 'payg',
    name: 'Paga lo que usas',
    tagline: 'Sin suscripciones, sin compromisos',
    priceLabel: 'Desde $5',
    priceSub: 'USD · compra por única vez',
    Icon: Zap,
    features: [
      'Paquetes de créditos a demanda',
      'Todos los modelos disponibles',
      'Sin vencimiento de créditos',
      'Exportación sin marca de agua',
    ],
    cta: 'Comprar créditos',
  },
  {
    id: 'studio',
    name: 'Creador Pro',
    tagline: 'Para quienes viven del contenido',
    priceLabel: 'Desde $29',
    priceSub: 'USD · paquete mensual',
    Icon: Crown,
    features: [
      'Gran volumen de créditos',
      'Prioridad en la cola de render',
      'Soporte dedicado',
      'Línea directa para feedback',
    ],
    cta: 'Hablemos',
  },
];

export const PricingOverlay = ({ onClose, onCTA }) => (
  <motion.div
    {...FADE}
    className="landing-overlay-card pricing"
    role="dialog"
    aria-labelledby="pricing-overlay-title"
    data-testid="landing-overlay-pricing"
  >
    <div className="landing-overlay-glow" aria-hidden="true" />
    <button
      className="landing-overlay-close"
      onClick={onClose}
      data-testid="landing-overlay-close"
      aria-label="Cerrar"
    >
      <X size={16} />
    </button>

    <div className="landing-overlay-eyebrow">
      <Sparkles size={14} />
      <span>Planes Studio MediaLabs</span>
    </div>

    <h2 id="pricing-overlay-title" className="landing-overlay-title small">
      Construye nuevos mundos <span className="accent">sin límites ni ataduras</span>
    </h2>

    <div className="landing-overlay-plans">
      {PLANS.map((plan) => {
        const { Icon } = plan;
        return (
          <article
            key={plan.id}
            className={`landing-plan-card ${plan.featured ? 'featured' : ''}`}
            data-testid={`landing-plan-${plan.id}`}
          >
            {plan.featured && (
              <div className="landing-plan-badge">Empezá por acá</div>
            )}
            <div className="landing-plan-head">
              <div className="landing-plan-icon">
                <Icon size={16} />
              </div>
              <div className="landing-plan-name-group">
                <h3 className="landing-plan-name">{plan.name}</h3>
                <p className="landing-plan-tagline">{plan.tagline}</p>
              </div>
            </div>

            <div className="landing-plan-price">
              <span className="landing-plan-price-main">{plan.priceLabel}</span>
              <span className="landing-plan-price-sub">{plan.priceSub}</span>
            </div>

            <ul className="landing-plan-features">
              {plan.features.map((f) => (
                <li key={f}>
                  <Check size={13} /> <span>{f}</span>
                </li>
              ))}
            </ul>

            <button
              className={`landing-plan-cta ${plan.featured ? 'primary' : 'ghost'}`}
              onClick={() => onCTA(plan.id)}
              data-testid={`landing-plan-cta-${plan.id}`}
            >
              {plan.cta}
            </button>
          </article>
        );
      })}
    </div>
  </motion.div>
);

/* ============================================================
   AnimatedPresenceWrapper — used by LandingPage
   ============================================================ */
export const LandingOverlayRoot = ({ active, onClose, onStudioCTA, onPricingCTA }) => (
  <AnimatePresence mode="wait">
    {active === 'studio' && (
      <StudioOverlay key="studio" onClose={onClose} onCTA={onStudioCTA} />
    )}
    {active === 'pricing' && (
      <PricingOverlay key="pricing" onClose={onClose} onCTA={onPricingCTA} />
    )}
  </AnimatePresence>
);
